import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { UserRole } from '@rhwb/types';

interface AppContextType {
  selectedRunner: string;
  userRole: UserRole;
  hybridToggle: 'myScore' | 'myCohorts';
  setSelectedRunner: (runner: string) => void;
  setUserRole: (role: UserRole) => void;
  setHybridToggle: (toggle: 'myScore' | 'myCohorts') => void;
  // Override functionality for admin users
  overrideEmail: string | null;
  setOverrideEmail: (email: string | null) => void;
  authenticatedEmail: string;
  setAuthenticatedEmail: (email: string) => void;
  // The effective email to use for dashboard/profile (override takes precedence)
  effectiveEmail: string;
  // Whether override mode is active
  isOverrideActive: boolean;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedRunner, setSelectedRunner] = useState<string>('');
  const [userRole, setUserRole] = useState<UserRole>('runner');
  const [hybridToggle, setHybridToggle] = useState<'myScore' | 'myCohorts'>('myCohorts');
  
  // Override email tracking
  const [overrideEmail, setOverrideEmail] = useState<string | null>(null);
  const [authenticatedEmail, setAuthenticatedEmail] = useState<string>('');

  // Compute effective email: override takes precedence over authenticated
  const effectiveEmail = useMemo(() => {
    return overrideEmail || authenticatedEmail;
  }, [overrideEmail, authenticatedEmail]);

  // Whether override mode is active
  const isOverrideActive = useMemo(() => {
    return overrideEmail !== null && overrideEmail !== '';
  }, [overrideEmail]);

  return (
    <AppContext.Provider value={{ 
      selectedRunner, 
      userRole, 
      hybridToggle, 
      setSelectedRunner, 
      setUserRole, 
      setHybridToggle,
      overrideEmail,
      setOverrideEmail,
      authenticatedEmail,
      setAuthenticatedEmail,
      effectiveEmail,
      isOverrideActive
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

/**
 * Builds in-app paths that keep an admin's email override. Override mode
 * lives in the `?email_id=` URL parameter (ProtectedRoute clears it whenever a
 * page loads without one), so every in-app navigation must carry it along.
 * Falls back to the current URL's email_id because a child page's effects run
 * before ProtectedRoute has copied it into context (e.g. an immediate
 * redirect on mount).
 */
export const useOverridePath = (): ((path: string) => string) => {
  const { overrideEmail } = useApp();
  const urlEmail = new URLSearchParams(useLocation().search).get('email_id');
  const email = overrideEmail || urlEmail;
  return React.useCallback(
    (path: string) =>
      email ? `${path}${path.includes('?') ? '&' : '?'}email_id=${encodeURIComponent(email)}` : path,
    [email]
  );
};

